import { CONFIG } from "../../../config";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

const CRMCONFIG = CONFIG.modules["crm"];
const API_HOST_CRM = CRMCONFIG.host;

// Development logging utility
const isDevelopment = process.env.NODE_ENV === 'development';
const devLog = (message, data) => {
  if (isDevelopment) {
    console.log(message, data);
  }
};

/**
 * Custom hook for fetching other status analytics data (Active, Pending, Unattempted)
 * @param {Object} queries - Query parameters for the API call
 * @param {Object} options - Additional configuration options
 * @param {boolean} options.enabled - Whether the query should be enabled
 * @param {number} options.staleTime - Time in milliseconds before data is considered stale
 * @param {number} options.cacheTime - Time in milliseconds to keep data in cache
 * @param {number} options.retry - Number of retry attempts on failure
 * @param {boolean} options.refetchOnWindowFocus - Whether to refetch on window focus
 * @returns {Object} React Query result object
 */
export const useGetOtherStatusAnalytics = (queries = {}, options = {}) => {
  // Validate inputs
  const validatedQueries = useMemo(() => {
    if (!queries || typeof queries !== 'object') {
      devLog('[useGetOtherStatusAnalytics] Invalid queries parameter, using empty object');
      return {};
    }
    return queries;
  }, [queries]);

  // Default options with user overrides
  const queryOptions = useMemo(() => ({
    enabled: true,
    refetchOnWindowFocus: false,
    retry: 2,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
    ...options
  }), [options]);

  // Memoized query key for better performance
  const queryKey = useMemo(() => {
    const sortedEntries = Object.entries(validatedQueries)
      .filter(([, value]) => value !== undefined && value !== null)
      .sort(([a], [b]) => a.localeCompare(b));

    return ["otherStatusAnalytics", ...sortedEntries.flat()];
  }, [validatedQueries]);

  // Memoized fetch function to prevent unnecessary re-renders
  const fetchOtherStatusAnalytics = useCallback(async () => {
    try {
      // Filter out undefined/null values from params
      const cleanParams = Object.fromEntries(
        Object.entries(validatedQueries).filter(([, value]) =>
          value !== undefined && value !== null && value !== ''
        )
      );

      devLog('[useGetOtherStatusAnalytics] Making API call with params:', cleanParams);

      const response = await axios.get(
        `${API_HOST_CRM}/analytics/other-status-analytics`,
        {
          params: cleanParams,
          timeout: 30000, // 30 second timeout
        }
      );

      devLog('[useGetOtherStatusAnalytics] Backend Data:', response.data);

      // Basic response validation
      if (!response.data) {
        throw new Error('No data received from server');
      }

      return response.data;
    } catch (error) {
      // Enhanced error handling with network detection
      const isNetworkError = !error.response && error.code === 'ENOTFOUND';
      const isTimeoutError = error.code === 'ECONNABORTED';

      let errorMessage = 'Error fetching other status analytics';

      if (isNetworkError) {
        errorMessage += ': Network connection failed';
      } else if (isTimeoutError) {
        errorMessage += ': Request timeout';
      } else if (error.response) {
        errorMessage += `: Server responded with ${error.response.status}`;
      }

      console.error(errorMessage, {
        originalError: error,
        params: validatedQueries,
        timestamp: new Date().toISOString()
      });

      throw new Error(errorMessage);
    }
  }, [validatedQueries]);

  return useQuery({
    queryKey,
    queryFn: fetchOtherStatusAnalytics,
    enabled: queryOptions.enabled,
    refetchOnWindowFocus: queryOptions.refetchOnWindowFocus,
    retry: queryOptions.retry,
    staleTime: queryOptions.staleTime,
    cacheTime: queryOptions.cacheTime,
    onError: (error) => {
      console.error("Error in useGetOtherStatusAnalytics:", {
        error: error.message,
        queries: validatedQueries,
        timestamp: new Date().toISOString()
      });
    },
    // Performance optimization: only select data if it exists
    select: (data) => data || null,
  });
};

/**
 * Custom hook for fetching other status analytics data with flexible query params
 *
 * Signature: `useGetOtherStatusAnalyticsRange(queries = {}, options = {})`
 *
 * @param {Object} queries - Query parameters to send to the API. Common keys:
 *   - `from`: string (YYYY-MM-DD)
 *   - `to`: string (YYYY-MM-DD)
 *   - other filter keys accepted by the backend (e.g. `data_type`)
 * @param {Object} options - Hook options (passed to react-query)
 * @param {boolean} options.enabled - Whether the query should be enabled
 * @param {number} options.staleTime - How long data is considered fresh (ms)
 * @param {number} options.cacheTime - How long to cache the data (ms)
 * @param {number} options.retry - Number of retry attempts on failure
 * @param {boolean} options.refetchOnWindowFocus - React-query refetch on window focus
 * @param {string} options.fetchKey - Optional key appended to the react-query key to force separation
 * @returns {Object} React Query result object
 */
export const useGetOtherStatusAnalyticsRange = (queries = {}, options = {}) => {

  // Validate queries
  const validatedQueries = (() => {
    if (!queries || typeof queries !== 'object') return {};
    return queries;
  })();

  const fetchOtherStatusAnalytics = async () => {
    try {
      // Filter out undefined/null/empty values from params
      const cleanParams = Object.fromEntries(
        Object.entries(validatedQueries).filter(([, value]) =>
          value !== undefined && value !== null && value !== ''
        )
      );

      devLog('[useGetOtherStatusAnalyticsRange] Making API call with params:', cleanParams);

      const response = await axios.get(
        `${API_HOST_CRM}/analytics/other-status-analytics`,
        {
          params: cleanParams,
          timeout: 30000, // 30 second timeout
        }
      );

      devLog('[useGetOtherStatusAnalyticsRange] Backend Data:', response.data);

      if (!response.data) throw new Error('No data received from server');

      return response.data;
    } catch (error) {
      const isNetworkError = !error.response && error.code === 'ENOTFOUND';
      const isTimeoutError = error.code === 'ECONNABORTED';

      let errorMessage = 'Error fetching other status analytics';

      if (isNetworkError) {
        errorMessage += ': Network connection failed';
      } else if (isTimeoutError) {
        errorMessage += ': Request timeout';
      } else if (error.response) {
        errorMessage += `: Server responded with ${error.response.status}`;
      }

      console.error(errorMessage, {
        originalError: error,
        params: validatedQueries,
        timestamp: new Date().toISOString()
      });

      throw new Error(errorMessage);
    }
  };

  // Build stable query key from validatedQueries and optional fetchKey
  const queryKey = (() => {
    const sortedEntries = Object.entries(validatedQueries)
      .filter(([, value]) => value !== undefined && value !== null)
      .sort(([a], [b]) => a.localeCompare(b));

    const base = ["otherStatusAnalyticsRange", ...sortedEntries.flat()];
    if (options.fetchKey) base.push(options.fetchKey);
    return base;
  })();

  return useQuery({
    queryKey,
    queryFn: fetchOtherStatusAnalytics,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
    retry: options.retry ?? 2,
    staleTime: options.staleTime ?? 1000 * 60 * 5,
    cacheTime: options.cacheTime ?? 1000 * 60 * 10,
    enabled: options.enabled ?? true,
    onError: (error) => {
      console.error("Error in useGetOtherStatusAnalyticsRange:", {
        error: error.message,
        params: validatedQueries,
        timestamp: new Date().toISOString()
      });
    },
    select: (data) => data || null,
  });
};
